import { createCanvas } from './loadImage';
import { getMaskFactor, type MaskChannel } from './mask';

export interface PixelRect { x: number; y: number; width: number; height: number }
export interface MaskRegion extends PixelRect { data: Uint8ClampedArray }

type Entry = {
  dependencies: readonly unknown[];
  value: unknown;
  bytes: number;
  release?: () => void;
};

/** A bounded cache for ONE static export. Never store changing video frames here. */
export class StaticRenderCache {
  private entries = new Map<string, Entry>();
  private sourceIds = new WeakMap<object, number>();
  private nextSourceId = 0;
  private bytes = 0;
  private disposed = false;

  constructor(private readonly byteLimit = 96 * 1024 * 1024) {}

  get byteLength(): number { return this.bytes; }

  /** Skip preparation when it would only add an uncached extra full-frame copy. */
  preparedCanvas(key: string, dependencies: readonly unknown[], width: number, height: number, create: () => HTMLCanvasElement): HTMLCanvasElement | null {
    const cached = this.lookup<HTMLCanvasElement>(key, dependencies);
    if (cached) return cached;
    if (this.bytes + width * height * 4 > this.byteLimit) return null;
    return this.canvas(key, dependencies, create);
  }

  canvas(key: string, dependencies: readonly unknown[], create: () => HTMLCanvasElement): HTMLCanvasElement {
    const cached = this.lookup<HTMLCanvasElement>(key, dependencies);
    if (cached) return cached;
    const value = create();
    this.retain(key, dependencies, value, value.width * value.height * 4, () => {
      value.width = 1;
      value.height = 1;
    });
    return value;
  }

  /** Original RGBA values at the requested render scale, not quantized alpha. */
  pixels(source: CanvasImageSource, width: number, height: number, rect: PixelRect): Uint8ClampedArray {
    const key = `pixels:${this.sourceId(source)}:${width}:${height}:${rect.x}:${rect.y}:${rect.width}:${rect.height}`;
    const cached = this.lookup<Uint8ClampedArray>(key, []);
    if (cached) return cached;
    const value = this.readPixels(source, width, height, rect);
    this.retain(key, [], value, value.byteLength);
    return value;
  }

  /** Exact bounds of all nonzero mask coverage, including antialiased edges. */
  mask(source: CanvasImageSource, width: number, height: number, channel: MaskChannel = 'luminance'): MaskRegion {
    const key = `mask:${this.sourceId(source)}:${width}:${height}:${channel}`;
    const cached = this.lookup<MaskRegion>(key, []);
    if (cached) return cached;
    const bounds = this.lookup<PixelRect>(`${key}:bounds`, []);
    const value = bounds
      ? { ...bounds, data: this.readPixels(source, width, height, bounds) }
      : cropMaskRegion(this.readPixels(source, width, height, { x: 0, y: 0, width, height }), width, height, channel);
    // Keep the tiny bounds record even when the RGBA cache budget is full, so
    // an uncached large mask is not rescanned twice per video frame.
    if (!bounds) this.retain(`${key}:bounds`, [], { x: value.x, y: value.y, width: value.width, height: value.height }, 0);
    this.retain(key, [], value, value.data.byteLength);
    return value;
  }

  dispose(): void {
    this.disposed = true;
    this.entries.forEach(entry => entry.release?.());
    this.entries.clear();
    this.sourceIds = new WeakMap();
    this.bytes = 0;
  }

  private sourceId(source: CanvasImageSource): number {
    let id = this.sourceIds.get(source);
    if (id === undefined) {
      id = ++this.nextSourceId;
      this.sourceIds.set(source, id);
    }
    return id;
  }

  private lookup<T>(key: string, dependencies: readonly unknown[]): T | undefined {
    if (this.disposed) throw new Error('Static render cache is already disposed.');
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    if (entry.dependencies.length === dependencies.length && entry.dependencies.every((item, i) => Object.is(item, dependencies[i]))) {
      return entry.value as T;
    }
    // Do not clear a canvas here: a caller may still be using it in this frame.
    // Dropping the cache's reference preserves that ownership and lets GC free it.
    this.entries.delete(key);
    this.bytes -= entry.bytes;
    return undefined;
  }

  private retain(key: string, dependencies: readonly unknown[], value: unknown, bytes: number, release?: () => void): void {
    // No eviction in the middle of a frame: retained surfaces can be referenced
    // by another prepared layer. Over-budget results simply remain uncached.
    if (this.bytes + bytes > this.byteLimit) return;
    this.entries.set(key, { dependencies: dependencies.slice(), value, bytes, release });
    this.bytes += bytes;
  }

  private readPixels(source: CanvasImageSource, width: number, height: number, rect: PixelRect): Uint8ClampedArray {
    if (rect.width <= 0 || rect.height <= 0) return new Uint8ClampedArray(0);
    const canvas = createCanvas(width, height);
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('2D context is not available for static export masks');
      // Match the original full-size draw before cropping. Drawing the image
      // directly into a smaller canvas can change filtering at crop boundaries.
      ctx.drawImage(source, 0, 0, width, height);
      return ctx.getImageData(rect.x, rect.y, rect.width, rect.height).data;
    } finally {
      canvas.width = 1;
      canvas.height = 1;
    }
  }
}

export function cropMaskRegion(pixels: Uint8ClampedArray, width: number, height: number, channel: MaskChannel): MaskRegion {
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (getMaskFactor(pixels, (y * width + x) * 4, channel) <= 0) continue;
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x); bottom = Math.max(bottom, y);
    }
  }
  if (right < left) return { x: 0, y: 0, width: 0, height: 0, data: new Uint8ClampedArray(0) };
  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;
  if (croppedWidth === width && croppedHeight === height) return { x: 0, y: 0, width, height, data: pixels };
  const data = new Uint8ClampedArray(croppedWidth * croppedHeight * 4);
  for (let row = 0; row < croppedHeight; row++) {
    const start = ((top + row) * width + left) * 4;
    data.set(pixels.subarray(start, start + croppedWidth * 4), row * croppedWidth * 4);
  }
  return { x: left, y: top, width: croppedWidth, height: croppedHeight, data };
}
